import { useEffect, useState } from "react";

export { holdCountdown } from "@gooner/shared";

/**
 * Milliseconds left until `deadline`, re-rendering once a second, 0 once it passes.
 *
 * ONE TICKER, because three surfaces count the same wait down — the badge on the glass,
 * the phone that scanned, and the phone that typed — and three hand-rolled intervals is
 * three chances to get the same thing subtly different.
 *
 * It CLEARS ITSELF AT ZERO. Every one of those surfaces is a page somebody leaves open:
 * a television on a wall for weeks, a remote in a pocket. An interval that keeps firing
 * after the thing it was counting has finished is a wakeup a second, forever, to render
 * a number that no longer changes.
 *
 * The deadline is LOCAL — computed by the caller from a duration the server reported, not
 * from a server instant. A television's clock is the one most likely to be minutes out,
 * and differencing a server timestamp against it renders nonsense or nothing.
 */
export function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(() =>
    deadline === null ? 0 : Math.max(0, deadline - Date.now()),
  );

  useEffect(() => {
    if (deadline === null) {
      setLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, deadline - Date.now());
      setLeft(remaining);
      return remaining;
    };
    if (tick() <= 0) return;
    const timer = setInterval(() => {
      if (tick() <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  return left;
}
